import React, { useEffect, useState, useRef } from 'react';
import ThemeBackground from './ThemeBackground';

/**
 * Lazily mounts the heavy canvas background after initial content is on screen.
 * Heuristics:
 *  - Wait for first image batch (imagesLoaded prop) OR
 *  - Fallback to requestIdleCallback / setTimeout (~400ms) whichever comes first.
 *  - Skip entirely on mobile and when headless (ThemeBackground already handles headless).
 */
export default function LazyThemeBackground({ imagesLoaded }) {
  const [ready, setReady] = useState(false);
  const scheduledRef = useRef(false);

  // Schedule background mount (only once)
  const scheduleMount = (delay = 0) => {
    if (scheduledRef.current || ready) return;
    scheduledRef.current = true;
    if (delay > 0) {
      setTimeout(() => setReady(true), delay);
    } else if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      window.requestIdleCallback(() => setReady(true), { timeout: 500 });
    } else {
      setTimeout(() => setReady(true), 300);
    }
  };

  // If images loaded early, mount as soon as next frame
  useEffect(() => {
    if (imagesLoaded && !ready) {
      scheduleMount(0);
    }
  }, [imagesLoaded, ready]);

  // Safety fallback: ensure mount after ~400ms even if no images yet (slow network)
  useEffect(() => {
    if (!ready) {
      scheduleMount(400);
    }
  }, [ready]);

  if (!ready) return null;
  return <ThemeBackground />;
}
